import { GlLabel, GlLink, GlIcon, GlButton, GlAvatarsInline, GlTooltip, GlTooltipDirective } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { __, s__, sprintf } from '~/locale';
import { isScopedLabel } from '~/lib/utils/common_utils';
import SafeHtml from '~/vue_shared/directives/safe_html';
import { titleInLinkSafeHtmlConfig } from '~/lib/dompurify';
import UserLinkWithTooltip from '../../../vue_shared/components/user_link_with_tooltip.vue?vue3';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import { STATE_OPEN, WIDGET_TYPE_ASSIGNEES, WIDGET_TYPE_LABELS, WORK_ITEM_TYPE_ROUTE_WORK_ITEM, METADATA_KEYS } from '~/work_items/constants';
import WorkItemLinkChildMetadata from '../../../../../../ee/app/assets/javascripts/work_items/components/shared/work_item_link_child_metadata.vue?vue3';
import RichTimestampTooltip from '../rich_timestamp_tooltip.vue?vue3';
import WorkItemTypeIcon from '../work_item_type_icon.vue?vue3';
import WorkItemStateBadge from '../work_item_state_badge.vue?vue3';
import { canRouterNav, findLinkedItemsWidget, findStatusWidget, getDisplayReference, getMetadataWidgetsFromWorkItem } from '../../utils.js?vue3';
import WorkItemRelationshipIcons from './work_item_relationship_icons.vue?vue3';
export default {
    name: 'WorkItemLinkChildContents',
    i18n: {
        confidential: __('Confidential'),
        created: __('Created'),
        closed: __('Closed'),
        remove: s__('WorkItem|Remove'),
        assignee: s__('WorkItem|Assignee')
    },
    components: {
        GlLabel,
        GlLink,
        GlIcon,
        GlButton,
        GlAvatarsInline,
        GlTooltip,
        RichTimestampTooltip,
        WorkItemLinkChildMetadata,
        WorkItemTypeIcon,
        WorkItemStateBadge,
        WorkItemRelationshipIcons,
        UserLinkWithTooltip
    },
    directives: {
        GlTooltip: GlTooltipDirective,
        SafeHtml
    },
    titleInLinkSafeHtmlConfig,
    inject: {
        preventRouterNav: {
            default: false
        },
        fullPath: {
            default: ''
        }
    },
    props: {
        childItem: {
            type: Object,
            required: true
        },
        canUpdate: {
            type: Boolean,
            required: true
        },
        isGroup: {
            type: Boolean,
            required: false,
            default: false
        },
        workItemFullPath: {
            type: String,
            required: true
        },
        hiddenMetadataKeys: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        contextualViewEnabled: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'click',
        'mouseout',
        'mouseover',
        'remove-child'
    ],
    computed: {
        shouldShowAssignees () {
            const showAssignee = this.assignees.length && !this.hiddenMetadataKeys.includes(METADATA_KEYS.ASSIGNEE);
            return showAssignee;
        },
        labels () {
            return this.metadataWidgets[WIDGET_TYPE_LABELS]?.labels?.nodes || [];
        },
        metadataWidgets () {
            return getMetadataWidgetsFromWorkItem(this.childItem);
        },
        assignees () {
            return this.metadataWidgets[WIDGET_TYPE_ASSIGNEES]?.assignees?.nodes || [];
        },
        assigneesCollapsedTooltip () {
            if (this.assignees.length > 2) {
                return sprintf(s__('WorkItem|%{count} more assignees'), {
                    count: this.assignees.length - 2
                });
            }
            return '';
        },
        allowsScopedLabels () {
            return this.metadataWidgets[WIDGET_TYPE_LABELS]?.allowsScopedLabels;
        },
        isChildItemOpen () {
            return this.childItem.state === STATE_OPEN;
        },
        childItemType () {
            return this.childItem.workItemType.name;
        },
        childItemTypeIconName () {
            return this.childItem.workItemType.iconName;
        },
        childItemIid () {
            return this.childItem.iid;
        },
        childItemWebUrl () {
            return this.childItem.webUrl;
        },
        childItemFullPath () {
            return this.childItem.namespace?.fullPath;
        },
        stateTimestamp () {
            return this.isChildItemOpen ? this.childItem.createdAt : this.childItem.closedAt;
        },
        stateTimestampTypeText () {
            return this.isChildItemOpen ? this.$options.i18n.created : this.$options.i18n.closed;
        },
        childItemTypeIconVariant () {
            return this.isChildItemOpen ? 'default' : 'subtle';
        },
        displayLabels () {
            return !this.hiddenMetadataKeys.includes(METADATA_KEYS.LABELS) && this.labels.length;
        },
        workItemStatus () {
            return findStatusWidget(this.childItem)?.status?.name;
        },
        showState () {
            return !this.hiddenMetadataKeys.includes(METADATA_KEYS.STATUS) && (!this.workItemStatus || !this.isChildItemOpen);
        },
        showBlockingRelationships () {
            return !this.hiddenMetadataKeys.includes(METADATA_KEYS.BLOCKED) && (this.blockingCount > 0 || this.blockedByCount > 0);
        },
        displayReference () {
            return getDisplayReference(this.workItemFullPath, this.childItem.reference);
        },
        linkedItemsWidget () {
            return findLinkedItemsWidget(this.childItem);
        },
        blockingCount () {
            return this.linkedItemsWidget?.blockingCount || 0;
        },
        blockedByCount () {
            return this.linkedItemsWidget?.blockedByCount || 0;
        },
        childItemUniqueId () {
            return `listItem-${this.childItemFullPath}/${getIdFromGraphQLId(this.childItem.id)}`;
        }
    },
    methods: {
        showScopedLabel (label) {
            return isScopedLabel(label) && this.allowsScopedLabels;
        },
        handleItemClick (e) {
            const workItem = this.childItem;
            if (e.metaKey || e.ctrlKey || e.shiftKey) {
                return;
            }
            const shouldDefaultNavigate = this.preventRouterNav || !canRouterNav({
                fullPath: this.fullPath,
                webUrl: workItem.webUrl,
                isGroup: this.isGroup,
                issueAsWorkItem: !this.isGroup
            });
            const hasListRoute = this.$router && this.$router.getRoutes().some((route)=>route.name === 'workItem');
            if (shouldDefaultNavigate || !hasListRoute) {
                this.$emit('click', e);
            } else {
                e.preventDefault();
                this.$router.push({
                    name: 'workItem',
                    params: {
                        iid: workItem.iid,
                        type: WORK_ITEM_TYPE_ROUTE_WORK_ITEM
                    }
                });
            }
        }
    }
};
